import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import {Field, getFormValues, reduxForm} from 'redux-form';
import * as validateRule from '../../validateRule.js';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {connect} from 'react-redux';

import LoginCommon, {checkInputEmpty, commonStyles, renderTextField} from './loginCommon.js';
import {PositiveButton} from '../../atoms/Buttons/PositiveButton.js';
import {commonOperations} from '../../../reducks/common/index.js';
import {authOperations} from '../../../reducks/auth/index.js';
import withRouter from '../../organisms/withRouter.js';

/**
 * ログイン画面:パスワードリセット(新パスワード入力) テンプレート。
 * 画面ID:SCZ1001
 */
class Container extends React.Component {
  /**
   * パスワード変更ボタンクリック時処理
   */
  doPasswordChange = async () => {
    const {doShowMessage} = this.props;

    // Valdationが動作していない事があるため、手動でチェックする
    if (!checkInputEmpty(this.createCheckTargets, doShowMessage)) {
      return;
    }

    const dialog = {
      message: {
        id: 'TKSCS00002C',
        values: ['パスワードを変更'],
      },
      action: this._execPasswordChange,
    };

    doShowMessage(dialog);
  };

  /**
   * 手動で未入力チェックを行う対象の入力項目情報を生成。
   *
   * @return {{name: string, value: string}[]} 入力項目情報
   */
  createCheckTargets = () => {
    const {
      values,
    } = this.props;

    // チェックする情報
    const checkTargets = [
      {name: '「新しいパスワード」', value: values.newPassword},
      {name: '「新しいパスワード(確認)」', value: values.newPasswordConfirm},
    ];

    return checkTargets;
  };

  /**
   * パスワード変更処理実施
   */
  _execPasswordChange = async () => {
    const {
      doLogout,
      doShowMessage,
      navigate,
      doPasswordReset,
      values,
    } = this.props;

    try {
      const result = await doPasswordReset(
          values.userName,
          values.code,
          values.newPassword,
      );

      switch (result) {
        case 'OK':
          doShowMessage({
            message: 'TKSCZ00020I',
            action: doLogout,
          });
          break;

        case 'CODE_EXPIRED':
          doShowMessage({
            message: 'TKSCZ00055E',
            action: () => {
              navigate('/resetPassword');
            },
          });
          break;

        case 'HISTORY_PASSWORD':
          doShowMessage({
            message: 'TKSCZ00057E',
            action: () => {
              navigate('/resetPassword');
            },
          });
          break;

        default:
          doShowMessage({message: 'TKSCS00001S'});
          break;
      }
    } catch (err) {
      doShowMessage({message: 'TKSCS00001S'});
    }
  };

  /**
   * 描画処理。
   * @return {JSX.Element} メニュー画面の要素
   */
  render() {
    const {classes, handleSubmit, values} = this.props;
    const userId = values.userName;
    return (
      <LoginCommon>
        <Grid container>

          <Grid item xs={12}>
            <Field
              name="userName"
              label="ユーザID"
              type="text"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              InputProps={{
                readOnly: true,
              }}
              validate={[
                validateRule.required,
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Field
              name="code"
              label="認証コード"
              type="text"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              InputProps={{
                readOnly: true,
              }}
              validate={[
                validateRule.required,
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Field
              name="newPassword"
              label="新しいパスワード"
              type="password"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              validate={[
                validateRule.required,
                validateRule.minLength(8),
                validateRule.maxLength(20),
                validateRule.password(userId),
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <div className={classes.typography}>
              <Typography>{'※8文字以上20文字以下の半角英数字記号であること'}</Typography>
              <Typography>{'※英字、数字、記号をすべて含むこと'}</Typography>
              <Typography>{'※ユーザIDを含まないこと'}</Typography>
              <Typography>{'※同一文字の4回以上連続を含まないこと'}</Typography>
              <Typography>{'※過去に使用したパスワードは一定期間再利用不可'}</Typography>
            </div>
          </Grid>

          <Grid item xs={12}>
            <Field
              name="newPasswordConfirm"
              label="新しいパスワード(確認)"
              type="password"
              className={classes.textField}
              required={true}
              margin="normal"
              variant="outlined"
              component={renderTextField}
              validate={[
                validateRule.required,
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <PositiveButton
              onClick={handleSubmit(this.doPasswordChange)}
              variant="contained"
              size="large"
              className={classes.button}
            >
              <span>パスワード変更</span>
            </PositiveButton>
          </Grid>
        </Grid>
      </LoginCommon>
    );
  }
}

/**
 * 複数項目のバリデーション。
 *
 * @param {object} values フォームの値
 * @return {object} エラー情報
 */
const validate = (values) => {
  const errors = {};

  const newPassword = values.newPassword;
  const newPasswordConfirm = values.newPasswordConfirm;

  // 新しいパスワード(確認)が未入力の場合は終了
  if (newPasswordConfirm == null) {
    return errors;
  }

  // パスワードと確認が一致しない場合
  if (newPassword != newPasswordConfirm) {
    errors['newPasswordConfirm'] = 'パスワードが一致しません';
  }

  return errors;
};

const FORM_NAME = 'NewPassword';

Container.propTypes = {
  classes: PropTypes.object.isRequired,
  doLogout: PropTypes.func,
  doPasswordReset: PropTypes.func,
  doShowMessage: PropTypes.func,
  handleSubmit: PropTypes.func,
  navigate: PropTypes.func,
  values: PropTypes.object,
};

const mapDispatchToProps = {
  doLogout: authOperations.doLogoutOperation,
  doClearGetParam: commonOperations.doClearGetParam,
  doPasswordReset: authOperations.doPasswordResetOperation,
  doShowMessage: commonOperations.doShowMessage,
  sendAuthenticationCode: authOperations.sendAuthenticationCodeOperation,
};

const mapStateToProps = (state) => {
  const qs = state.common.getParam;

  return {
    initialValues: {
      userName: qs.user_name,
      code: qs.confirmation_code,
      newPassword: null,
      newPasswordConfirm: null,
    },
  };
};

Container = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
  validate,
})(connect((state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
  };
})(Container));
export default withStyles(commonStyles)(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withRouter(Container)),
);
